import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Button, Grid, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { Authorization, useUser, graphService as coreGraphService } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import { ApplicationRole } from "constants/ApplicationRole";
import ControlledTextField from "components/inputs/ControlledTextField";
import type { Commentaire } from "models";
import { communesService } from "services";
import { withComponentErrorBoundary, useSnackbarErrorHandler } from "utils/errorHandling";
import ChatOutlined from "@mui/icons-material/ChatOutlined";
import { grey } from "@mui/material/colors";
import CommuneCommentaireInformation from "./CommuneCommentaireInformation";
import EditIcon from "@mui/icons-material/Edit";

interface CommuneCommentaireProps {
  idCommune: string;
  commentaire?: Commentaire;
}
function CommuneCommentaire({
  idCommune,
  commentaire: commentaireFromProps = { valeur: "" },
}: Readonly<CommuneCommentaireProps>): ReactNode {
  const [commentaire, setCommentaire] = useState<Commentaire>(commentaireFromProps);
  const { isRoles } = useUser();
  const { catchErrors, isLoading: isSending } = useSnackbarErrorHandler();

  const [auteur, setAuteur] = useState<User | undefined>(undefined);

  const [isEditing, setIsEditing] = useState(false);
  const { handleSubmit, control, reset, formState } = useForm<Commentaire>({
    shouldFocusError: false,
  });
  useEffect(() => {
    reset(commentaire);
    if (commentaire.idAuteur == null) {
      setAuteur(undefined);
    } else {
      void catchErrors(async () => {
        const agent = await coreGraphService.getUser(commentaire.idAuteur);
        setAuteur(agent);
      });
    }
  }, [reset, commentaire, catchErrors]);

  const sendData: SubmitHandler<Commentaire> = async function ({
    valeur,
  }: Commentaire): Promise<void> {
    if (!formState.isDirty) {
      return;
    }
    await catchErrors(async () => {
      const _commentaire = await communesService.postCommuneCommentaire(idCommune, { valeur });
      setCommentaire(_commentaire);
      setIsEditing(false);
    });
  };

  const hasCommentaire = commentaire.valeur != null && commentaire.valeur !== "";

  if (!hasCommentaire && !isRoles(ApplicationRole.COMMENTAIRES_WRITE)) {
    return null;
  }

  return (
    <Grid
      container
      spacing={2}
      component="form"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(sendData)}
      size={12}
      sx={{
        alignItems: "flex-start",
      }}>
      {isEditing ? (
        <Grid size={{ xs: 12, sm: "grow" }}>
          <ControlledTextField control={control} name="valeur" label="Commentaire" multiline />
        </Grid>
      ) : (
        hasCommentaire && (
          <>
            <Grid size="auto">
              <ChatOutlined sx={{ color: grey[700] }} />
            </Grid>
            <Grid size={{ xs: 12, sm: "grow" }}>
              <Typography sx={{ whiteSpace: "break-spaces" }}>{commentaire.valeur}</Typography>
              <CommuneCommentaireInformation
                auteurDisplayName={auteur?.displayName}
                dateCreation={commentaire.dateCreation}
              />
            </Grid>
          </>
        )
      )}
      <Grid container size={12}>
        <Authorization roles={ApplicationRole.COMMENTAIRES_WRITE}>
          {isEditing ? (
            <>
              <Button
                variant="text"
                color="error"
                onClick={(e) => {
                  e.preventDefault();
                  reset(commentaireFromProps);
                  setIsEditing(!isEditing);
                }}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                loading={isSending}
                disabled={!formState.isDirty}
                loadingPosition="start">
                Enregistrer
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color={isEditing ? "primary" : "secondary"}
              type={isEditing ? "button" : "submit"}
              startIcon={hasCommentaire ? <EditIcon /> : <ChatOutlined />}
              onClick={() => {
                setIsEditing(!isEditing);
              }}>
              {hasCommentaire ? "Modifier le commentaire" : "Ajouter un commentaire"}
            </Button>
          )}
        </Authorization>
      </Grid>
    </Grid>
  );
}

const CommuneCommentaireWithErrorBoundary = withComponentErrorBoundary(CommuneCommentaire);

export default CommuneCommentaireWithErrorBoundary;
